import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api, getBlob } from "../data/client";
import { scopedKey } from "../data/keys";
import { toWire } from "./query";

/* ---------- types (single home; later tasks import from here) ---------- */

export type Periodicity = "Monthly" | "Bi-Monthly" | "Quarterly" | "Bi-Annually" | "Annually" | "None";
export type Sort = "newest" | "name_asc" | "name_desc" | "recently_edited" | "next_visit";
export type Gender = "male" | "female" | "male_only" | "female_only";
export type VisitStatusFilter = "never_visited" | "postponed";
export type PerPage = 25 | 50 | 100;

export type ContactQuery = {
  q?: string;
  needsAttention?: boolean;
  /** 2E: contacts with an open "possible duplicate" mark (only with `can.see_duplicates`). */
  possibleDuplicate?: boolean;
  visitStatus?: VisitStatusFilter[];
  dueBy?: string;
  notVisitedSince?: string;
  schedule?: Periodicity[];
  categories?: { include?: number[]; exclude?: number[]; none?: boolean };
  projects?: { include?: number[]; exclude?: number[] };
  zip?: string[];
  gender?: Gender;
  sort?: Sort;
};

export type ListState = ContactQuery & { page: number; perPage: PerPage };

export type VisitStatusKey =
  | "never_visited"
  | "overdue"
  | "postponed"
  | "due"
  | "visited_recently"
  | "no_schedule"
  | "on_track";

export type ContactRow = {
  id: number;
  name: string;
  main_person: { id: number; name: string; phone?: string | null } | null;
  /** Absent without read_contact_info; null when the contact has no primary address. */
  address?: {
    line: string | null;
    street_line: string | null;
    region_line: string | null;
    is_manual: boolean;
    is_text_only: boolean;
    is_work: boolean;
  } | null;
  /** `address_reported` (2D) comes first: an approver marked an address problem a visit report told of. */
  attention: "address_not_found" | "address_locating" | "address_reported" | null;
  schedule: { periodicity: Periodicity | null; key: string };
  /** Absent without read_history. */
  visits?: {
    status: VisitStatusKey;
    days: number | null;
    last_visit_date: string | null;
    next_planned_date: string | null;
  };
  categories: { items: { id: number; name: string }[]; total: number; ids: number[] };
  pending_changes?: boolean;
  /**
   * The contact's newest open "possible duplicate" mark (2E, R3/R4). Present only with `meta.can.see_duplicates` and
   * only on a marked contact (absent, never null, otherwise). `other` only when the other contact is in the reader's
   * book, else null.
   */
  possible_duplicate?: PossibleDuplicateMark;
  can: { update: boolean; delete: boolean };
};

export type PossibleDuplicateMark = {
  other: { id: number; name: string } | null;
  /** 2F adds `scan` (found by "Find possible duplicates…"). */
  source: "import" | "added" | "scan";
  /** ISO 8601. */
  at: string;
  matched: ("name" | "phone" | "address")[];
};

export type ContactsCan = {
  report: boolean;
  update: boolean;
  delete: boolean;
  download: boolean;
  filter_projects: boolean;
  add: boolean;
  import: boolean;
  manage_categories: boolean;
  /** Today's category list gate (read_categories or Maps & Routes access): the category names, filters and pickers. */
  see_categories: boolean;
  see_contact_info: boolean;
  see_history: boolean;
  /** 2E: the "possible duplicate" mark, chip, filter and count (account managers with the full role). */
  see_duplicates: boolean;
  /** 2E: Contact settings (the visit-status days). */
  contact_settings: boolean;
};

export type ListMeta = {
  page: number;
  per_page: number;
  last_page: number;
  filtered: number;
  total: number;
  sort: Sort;
  can: ContactsCan;
};

export type ContactList = { data: ContactRow[]; meta: ListMeta };

export type ContactCounts = {
  matching: number;
  total: number;
  needs_attention: number;
  /** The Map's "addresses not on the map": real problems only, and the ones a geocoder is still placing (not a problem yet). */
  unplaced?: number;
  placing?: number;
  never_visited?: number;
  postponed?: number;
  categories: Record<string, number>;
  projects?: Record<string, number>;
  /** 2E: open marks in the reader's book; present only with `can.see_duplicates`. */
  possible_duplicates?: number;
};

export type BulkPreview = { count: number; persons: number; addresses?: number; visits?: number };

export type Category = { id: number; name: string };
export type Project = { id: number; name: string; closed: boolean };

/** What a bulk action applies to: these ids, or every match of a query minus some. */
export type Selection =
  | { ids: number[] }
  | { all: true; query: ContactQuery; excludedIds?: number[] };

export type BulkScheduleResult = { affected: number; queued?: boolean };

/* ---------- helpers ---------- */

const BASE = "/api/auth/next";

const toWireSelection = (s: Selection): Record<string, unknown> =>
  "ids" in s
    ? { ids: s.ids }
    : {
        all: true,
        query: toWire(s.query),
        ...(s.excludedIds && s.excludedIds.length > 0 ? { excluded_ids: s.excludedIds } : {}),
      };

const listParams = (s: ListState) => ({ ...toWire(s), page: s.page, per_page: s.perPage });

/* ---------- queries ---------- */

export const useContactList = (state: ListState, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  const params = listParams(state);
  return useQuery({
    queryKey: scopedKey(ws, "contacts", "list", params),
    queryFn: () => api.get<ContactList>(`${BASE}/households`, params),
    // Keep the old page while the next loads, but never another workspace's.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === ws ? prev : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

export const PHONE_PAGE_SIZE = 25;

/** The phone list: pages of 25 from `state.page` on, each "Show 25 more" appends the next one. */
export const useContactListInfinite = (state: ListState, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  const params = { ...toWire(state), per_page: PHONE_PAGE_SIZE };
  return useInfiniteQuery({
    queryKey: scopedKey(ws, "contacts", "list", "infinite", { ...params, from: state.page }),
    queryFn: ({ pageParam }) => api.get<ContactList>(`${BASE}/households`, { ...params, page: pageParam }),
    initialPageParam: state.page,
    getNextPageParam: (last) => (last.meta.page < last.meta.last_page && last.data.length > 0 ? last.meta.page + 1 : undefined),
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === ws ? prev : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

export const useContactCounts = (query: ContactQuery, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  // Order and paging don't change any count: leave them out so a sort change doesn't refetch.
  const { sort, page, perPage, ...counted } = query as ListState;
  const params = toWire(counted);
  return useQuery({
    queryKey: scopedKey(ws, "contacts", "counts", params),
    queryFn: async () => (await api.get<{ data: ContactCounts }>(`${BASE}/households/counts`, params)).data,
    // Keep the last numbers while the next query loads (no flash of 0), never another workspace's.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === ws ? prev : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** Only for members with `can.see_categories` (the server answers 403 to everyone else): pass it as `enabled`. */
export const useCategories = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contacts", "categories"),
    queryFn: async () => (await api.get<{ data: Category[] }>(`${BASE}/categories`)).data,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** Managers only; the server answers 403 PROJECTS_FILTER_MANAGERS_ONLY to everyone else. */
export const useProjects = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contacts", "projects"),
    queryFn: async () => (await api.get<{ data: Project[] }>(`${BASE}/projects`)).data,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

export const useCategoryReach = (ids: number[], opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "contacts", "reach", ids),
    queryFn: async () => (await api.get<{ data: Record<string, number> }>(`${BASE}/categories/reach`, { ids })).data,
    enabled: ws !== null && ids.length > 0 && (opts.enabled ?? true),
  });
};

/* ---------- mutations ---------- */

/**
 * Forget queries (a deleted contact, or one the user can no longer see) once no screen shows them any more: removed at
 * once when nothing watches them, else when the last screen showing them lets go (a closing dialog keeps its contact
 * on screen until it has gone). Never invalidated meanwhile, so no request for them starts (it would answer 404).
 */
export const dropWhenUnwatched = (client: QueryClient, key: unknown[]) => {
  const cache = client.getQueryCache();
  const watched = () => cache.findAll({ queryKey: key }).some((q) => q.getObserversCount() > 0);
  if (!watched()) { client.removeQueries({ queryKey: key }); return; }
  const stop = cache.subscribe((e) => {
    if (e.type !== "observerRemoved" || watched()) return;
    stop();
    client.removeQueries({ queryKey: key });
  });
};

/**
 * After a list or bulk write: the list (rows, counts, pickers) and every contact already loaded — its page, history
 * and single lines all sit under `["ws", ws, "contact"]` — so a contact opened again never shows the old values or
 * sends an old `version`. Deleted contacts (`gone`: their ids, or "all" when the selection was a query) are dropped
 * instead (`dropWhenUnwatched`), never fetched again, and so is their visit history (`["ws", ws, "visits", id]`).
 */
export const refreshAfterListWrite = (client: QueryClient, ws: number | null, gone?: number[] | "all") => {
  const contacts = ["ws", ws, "contact"];
  const visits = ["ws", ws, "visits"];
  if (gone === "all") { dropWhenUnwatched(client, contacts); dropWhenUnwatched(client, visits); }
  else gone?.forEach((id) => { dropWhenUnwatched(client, [...contacts, id]); dropWhenUnwatched(client, [...visits, id]); });
  const isGone = (key: readonly unknown[]) => gone === "all" || (gone?.includes(key[3] as number) ?? false);
  return Promise.all([
    client.invalidateQueries({ queryKey: ["ws", ws, "contacts"] }),
    client.invalidateQueries({ queryKey: contacts, predicate: (q) => !isGone(q.queryKey) }),
  ]);
};

const useBulk = <TVars, TResult>(run: (vars: TVars) => Promise<TResult>, gone?: (vars: TVars) => number[] | "all") => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (_r, vars) => refreshAfterListWrite(client, ws, gone?.(vars)),
  });
};

/** Read-only count of what a selection would touch; changes nothing, so it does not invalidate. */
export const useBulkPreview = () =>
  useMutation({
    mutationFn: async (selection: Selection) =>
      (await api.post<{ data: BulkPreview }>(`${BASE}/households/bulk/preview`, { selection: toWireSelection(selection) }))
        .data,
  });

export const useBulkSchedule = () =>
  useBulk(async (v: { selection: Selection; periodicity: Periodicity }) =>
    (
      await api.post<{ data: BulkScheduleResult }>(`${BASE}/households/bulk/schedule`, {
        selection: toWireSelection(v.selection),
        periodicity: v.periodicity,
      })
    ).data,
  );

export const useBulkCategories = () =>
  useBulk(async (v: { selection: Selection; addCategoryIds?: number[]; removeCategoryIds?: number[] }) =>
    (
      await api.post<{ data: { affected: number } }>(`${BASE}/households/bulk/categories`, {
        selection: toWireSelection(v.selection),
        ...(v.addCategoryIds?.length ? { add_category_ids: v.addCategoryIds } : {}),
        ...(v.removeCategoryIds?.length ? { remove_category_ids: v.removeCategoryIds } : {}),
      })
    ).data,
  );

export const useBulkDelete = () =>
  useBulk(async (v: { selection: Selection; confirmCount: number }) =>
    (
      await api.post<{ data: { affected: number } }>(`${BASE}/households/bulk/delete`, {
        selection: toWireSelection(v.selection),
        confirm_count: v.confirmCount,
      })
    ).data,
  (v) => ("ids" in v.selection ? v.selection.ids : "all"),
  );

/** How long the object URL lives after the click: some browsers read the file only after click() returns. */
export const REVOKE_AFTER_MS = 10_000;

/** Downloads the book in screen order (same filters and sort), saved through an object URL. */
export const useDownload = () => {
  const mutation = useMutation({
    mutationFn: async (v: { state: ContactQuery; lang: string }) => {
      const { blob, filename } = await getBlob(`${BASE}/households/download`, { ...toWire(v.state), lang: v.lang });
      const href = URL.createObjectURL(blob);
      try {
        const a = document.createElement("a");
        a.href = href;
        a.download = filename ?? (v.lang.startsWith("he") ? "households_data.xlsx" : "households_data.csv");
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(href), REVOKE_AFTER_MS);
      }
    },
  });
  const { mutateAsync } = mutation;
  return { ...mutation, download: (state: ContactQuery, lang: string): Promise<void> => mutateAsync({ state, lang }) };
};
